import type { Schema } from 'yup';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import type { DefaultValues, Resolver, ValidationMode } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import type { ZodType } from 'zod';

import type { DeepPartial } from '@luna/utils-ts';
import { logger } from '@luna/logger';

import { getResolver } from './ResolverService';
import type { CustomFieldErrors } from './FormValidationError';
import { ValidationListener } from './ValidationListener';
import { FormDevTool } from './dev/FormDevTool';
import { Form } from './Form';
import type { SubmitHandler, TypedFieldName } from './type';
import type { LunaFormContextValue } from './LunaFormContext';
import { LunaFormContext } from './LunaFormContext';
import { setErrors } from './ErrorsService';

export interface FormProps<FormValue> {
    /**
     * Submit handler when form is submitted (and valid)
     * @param values of the form
     */
    onSubmit: SubmitHandler<FormValue>;
    children: ReactNode;
    /**
     * Initial values of the form. When defaultValues are not provided, initialValues are used also as default values.
     */
    initialValues?: DeepPartial<FormValue>;
    /**
     * Default values of the form. (Values into which the form fields are reset).
     */
    defaultValues?: DeepPartial<FormValue>;

    initialErrors?: Partial<Record<TypedFieldName<FormValue>, string>>;
    /**
     * Yup validation schema. Preferably, Schema<FormValue> in your code to unsure type safety.
     *
     * Schema is also responsible for data transformation for submit hook handler - make sure the schema is correct,
     * if submit values are lost and/or transformed in a wrong way.
     *
     * Tip: If your form has a file upload, and it's being lost - try using "mixed<File>()" in the schema for the file property.
     */
    /** @deprecated newValidationSchema() acts as its replacement */
    validationSchema?: Schema<any>;
    newValidationSchema?: ZodType<FormValue>;
    /**
     * Flag if form should be validated on each change - can have performance impact.
     */
    validateOnChange?: boolean;
    /**
     * Flag if form should be validated on blur. Form is validated on blur only by default.
     */
    validateOnBlur?: boolean;
    /**
     * Flag if validation of the entire form should be triggered programmatically after every value change via attaching validation listener.
     * The validation listener is attached by default in order to override the default behaviour of react-hook-form that validates changed fields only.
     */
    attachValidationListener?: boolean;
    /**
     * Flag if form should be reset when initialValues gets updated after the form is rendered.
     */
    enableReinitialize?: boolean;
    /**
     * Toggle to wrap children inside <Form /> automatically, if disabled, you are responsible to render <Form /> component by yourself
     */
    shouldRenderForm?: boolean;
    /**
     * Data selector of the <form> element
     */
    'data-cy'?: string;
    className?: string;
    formId?: string;
    autoComplete?: 'on' | 'off';
    autoScrollToError?: boolean;
    autoFocusOnError?: boolean;
}

function getValidationMode({
    validateOnChange,
    validateOnBlur,
}: {
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
}): keyof ValidationMode {
    if (validateOnChange === false && validateOnBlur === false) {
        return 'onSubmit';
    }

    if (validateOnChange && validateOnBlur) {
        return 'all';
    }
    if (validateOnChange) {
        return 'onChange';
    }
    return 'onBlur';
}

export function getReValidationMode({
    validateOnChange,
    validateOnBlur,
}: {
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
}) {
    if (validateOnChange && validateOnBlur) {
        return undefined;
    }

    if (validateOnChange) {
        return 'onChange';
    }

    return 'onBlur';
}

/**
 * LunaForm component that wraps react-hook-form with additional functionality.
 * It provides a familiar LunaForm-like API while leveraging react-hook-form's performance.
 *
 * @template FormValue The type of the form values.
 * @param {FormProps<FormValue>} props The props for the LunaForm component.
 * @returns {ReactNode} The LunaForm form wrapper.
 *
 * @example
 * ```tsx
 * import { LunaForm } from '@luna/form2';
 * import * as Yup from 'yup';
 *
 * interface FormData {
 *   email: string;
 *   password: string;
 * }
 *
 * const validationSchema = Yup.object().shape({
 *   email: Yup.string().email('Invalid email').required('Required'),
 *   password: Yup.string().min(6, 'Too short').required('Required'),
 * });
 *
 * const MyForm = () => (
 *   <LunaForm<FormData>
 *     initialValues={{ email: '', password: '' }}
 *     validationSchema={validationSchema}
 *     onSubmit={async (values, { setErrors }) => {
 *       try {
 *         await submitToAPI(values);
 *       } catch (error) {
 *         setErrors({ email: 'Submission failed' });
 *       }
 *     }}
 *   >
 *    <CardGroup>
 *     <EmailFormFields />
 *     <Button type="submit">
 *         Submit
 *     </Button>
 *    </CardGroup>
 *   </LunaForm>
 * );
 * ```
 */
export const LunaForm = <FormValue extends Record<string, any>>({
    onSubmit,
    initialValues,
    defaultValues,
    initialErrors,
    validationSchema,
    newValidationSchema,
    validateOnBlur,
    validateOnChange,
    children,
    'data-cy': dataCy,
    attachValidationListener = true,
    enableReinitialize = false,
    className,
    formId,
    autoComplete = 'on',
    shouldRenderForm = true,
    autoScrollToError = true,
    autoFocusOnError = true,
}: FormProps<FormValue>): ReactNode => {
    const validationMode = getValidationMode({ validateOnBlur, validateOnChange });
    const isValidationDisabledOutsideSubmit =
        validateOnBlur === false && validateOnChange === false;

    const resolver = getResolver(validationSchema, newValidationSchema);
    const form = useForm<FormValue>({
        defaultValues: (defaultValues ?? initialValues) as DefaultValues<FormValue>,
        ...(defaultValues && { values: initialValues as FormValue }),
        mode: validationMode,
        reValidateMode: getReValidationMode({ validateOnChange, validateOnBlur }),
        resolver: resolver
            ? ((async (data: FormValue, context: unknown) => {
                  try {
                      return await resolver(
                          data,
                          context,
                          form.formState.errors as CustomFieldErrors,
                      );
                  } catch (error) {
                      logger.error(error);
                      // when yup validation unexpectedly throws,
                      // let's try to fallback to server validation
                      return { values: form.getValues(), errors: {} };
                  }
              }) as unknown as Resolver<FormValue>)
            : undefined,
        criteriaMode: 'all',
        resetOptions: {
            keepDefaultValues: true,
        },
    });

    const initialValuesString = JSON.stringify(initialValues);
    useEffect(() => {
        const formValues = JSON.stringify(form.getValues());
        if (enableReinitialize && formValues !== initialValuesString) {
            form.reset(initialValues as FormValue);
            logger.debug('Form values has been reset');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableReinitialize, initialValuesString]);

    useEffect(() => {
        if (!initialErrors) {
            return;
        }
        setErrors(form, initialErrors);
    }, []);

    const value = useMemo(() => {
        return {
            onSubmit,
            form,
            autoScrollToError,
            autoFocusOnError,
        };
    }, [form, onSubmit, autoScrollToError, autoFocusOnError]);

    return (
        <>
            {__DEV__ && <FormDevTool control={form.control} />}
            <FormProvider {...form}>
                {attachValidationListener && !isValidationDisabledOutsideSubmit && (
                    <ValidationListener />
                )}
                {/* noValidate disables HTML5 validations */}
                <LunaFormContext.Provider value={value as LunaFormContextValue}>
                    {shouldRenderForm && (
                        <Form
                            id={formId}
                            dataCy={dataCy}
                            className={className}
                            autoComplete={autoComplete}
                        >
                            {children}
                        </Form>
                    )}
                    {!shouldRenderForm && children}
                </LunaFormContext.Provider>
            </FormProvider>
        </>
    );
};
